import { useState, useEffect, useRef, useCallback } from 'react';
import { Building2, Phone, Users } from 'lucide-react';
import type { Town } from '@shared/types/town';
import { formatNumber } from '@shared/utils/format';
import { CATEGORY_OPTIONS_MAP, ALL_CATEGORIES, ALL_ORDERERS, ALL_SUB_APPS, ALL_CONTRACT_TYPES } from '@shared/constants/categoryOptions';

const CONSTRUCTION_STAFF_OPTIONS = ['홍승우', '정찬은', '이승현', '장성민', '이현철', '확인불가'];
const SETTING_STAFF_OPTIONS = ['이현진', '류지민', '장지안', '김호정', '박유찬', '확인불가'];
const INSTALLATION_TEAM_OPTIONS = [
    'FB시스템', 'GN TECH', 'S&P', 'YS테크', '네이슨', '동영', '미래정보통신',
    '버티스', '세명이앤지', '씨큐앤비', '에스케이테크', '에어제트', '원우전자통신',
    '유엔솔루션', '이시스시스템', '제이케이시스템', '제주업체', '탑시큐리티',
    '태호네트워크', '태양통신(동우통신)', '통신업체', '하나통신', '확인불가',
];

interface BasicInfoTabProps {
    town: Town;
    onSave: (data: Record<string, unknown>) => void;
    isSaving: boolean;
    saveSignal?: number;
}

export default function BasicInfoTab({ town, onSave, isSaving, saveSignal }: BasicInfoTabProps) {
    const [form, setForm] = useState({
        address: '',
        household_count: '',
        active_households: '',
        active_users_count: '',
        office_phone: '',
        office_fax: '',
        office_email: '',
        vpn_number: '',
        category: '',
        orderer: '',
        sub_app: '',
        contract_type: '',
        construction_staff: '',
        setting_staff: '',
        installation_team: '',
    });

    const isInitialMount = useRef(true);

    useEffect(() => {
        setForm({
            address: town.address ?? '',
            household_count: String(town.household_count ?? 0),
            active_households: String(town.active_households ?? 0),
            active_users_count: String(town.active_users_count ?? 0),
            office_phone: town.office_phone ?? '',
            office_fax: town.office_fax ?? '',
            office_email: town.office_email ?? '',
            vpn_number: town.vpn_number ?? '',
            category: town.category ?? '',
            orderer: town.orderer ?? '',
            sub_app: town.sub_app ?? '',
            contract_type: town.contract_type ?? '',
            construction_staff: town.construction_staff ?? '',
            setting_staff: town.setting_staff ?? '',
            installation_team: town.installation_team ?? '',
        });
    }, [town]);

    const doSave = useCallback(() => {
        const payload: Record<string, string | number | null> = {};
        for (const [key, value] of Object.entries(form)) {
            if (key === 'active_households' || key === 'active_users_count') continue;
            if (key === 'household_count') {
                payload[key] = value ? Number(value) : 0;
            } else {
                payload[key] = value || null;
            }
        }
        onSave(payload);
    }, [form, onSave]);

    // Watch saveSignal from footer button
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        if (saveSignal !== undefined && saveSignal > 0) {
            doSave();
        }
    }, [saveSignal]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleChange = (field: string, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    // 구분에 따른 연동 옵션
    const categoryConfig = form.category ? CATEGORY_OPTIONS_MAP[form.category] : null;
    const ordererOptions = categoryConfig ? categoryConfig.orderers : ALL_ORDERERS;
    const subAppOptions = categoryConfig ? categoryConfig.subApps : ALL_SUB_APPS;
    const contractTypeOptions = categoryConfig ? categoryConfig.contractTypes : ALL_CONTRACT_TYPES;

    const inputClass =
        'w-full px-3 py-2 text-sm border border-[#E6E9ED] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1ABB9C]/20 focus:border-[#1ABB9C] transition';
    const disabledClass =
        'w-full px-3 py-2 text-sm border border-[#E6E9ED] rounded-lg bg-[#F5F7FA] text-[#73879C]';
    const labelClass = 'block text-[11px] font-medium text-[#ADB5BD] mb-1';
    const selectClass =
        'w-full px-3 py-2 text-sm border border-[#E6E9ED] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#1ABB9C]/20 focus:border-[#1ABB9C] transition';

    return (
        <div>
            {/* 단지 기본 */}
            <div className="mb-7">
                <h3 className="text-xs font-bold text-[#2A3F54] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Building2 className="w-3.5 h-3.5 text-[#1ABB9C]" /> 단지 기본
                </h3>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className={labelClass}>
                            단지명 <span className="text-blue-400">(DB)</span>
                        </label>
                        <input type="text" value={town.name} disabled className={disabledClass} />
                    </div>
                    <div>
                        <label className={labelClass}>세대수</label>
                        <input
                            type="number"
                            value={form.household_count}
                            onChange={(e) => handleChange('household_count', e.target.value)}
                            className={inputClass}
                        />
                    </div>
                    <div className="col-span-2">
                        <label className={labelClass}>주소</label>
                        <input
                            type="text"
                            value={form.address}
                            onChange={(e) => handleChange('address', e.target.value)}
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>사용 세대수</label>
                        <input
                            type="text"
                            value={`${formatNumber(Number(form.active_households))}세대`}
                            disabled
                            className={disabledClass}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>사용자 수</label>
                        <input
                            type="text"
                            value={`${formatNumber(Number(form.active_users_count))}명`}
                            disabled
                            className={disabledClass}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>VPN 번호</label>
                        <input
                            type="text"
                            value={form.vpn_number}
                            onChange={(e) => handleChange('vpn_number', e.target.value)}
                            placeholder="10.75.0.XXX"
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>구분</label>
                        <select
                            value={form.category}
                            onChange={(e) => handleChange('category', e.target.value)}
                            className={selectClass}
                        >
                            <option value="">선택</option>
                            {ALL_CATEGORIES.map((o) => (
                                <option key={o} value={o}>{o}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* 관리사무소 정보 */}
            <div className="mb-7">
                <h3 className="text-xs font-bold text-[#2A3F54] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Phone className="w-3.5 h-3.5 text-[#1ABB9C]" /> 관리사무소 정보
                </h3>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className={labelClass}>전화번호</label>
                        <input
                            type="tel"
                            value={form.office_phone}
                            onChange={(e) => handleChange('office_phone', e.target.value)}
                            placeholder="02-0000-0000"
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>팩스번호</label>
                        <input
                            type="tel"
                            value={form.office_fax}
                            onChange={(e) => handleChange('office_fax', e.target.value)}
                            placeholder="02-0000-0000"
                            className={inputClass}
                        />
                    </div>
                    <div className="col-span-2">
                        <label className={labelClass}>단지 공용 메일</label>
                        <input
                            type="email"
                            value={form.office_email}
                            onChange={(e) => handleChange('office_email', e.target.value)}
                            placeholder="admin@example.com"
                            className={inputClass}
                        />
                    </div>
                </div>
            </div>

            {/* 담당자 · 업체 */}
            <div>
                <h3 className="text-xs font-bold text-[#2A3F54] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Users className="w-3.5 h-3.5 text-[#1ABB9C]" /> 담당자 · 업체
                </h3>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className={labelClass}>발주처</label>
                        <select
                            value={form.orderer}
                            onChange={(e) => handleChange('orderer', e.target.value)}
                            className={selectClass}
                        >
                            <option value="">선택</option>
                            {ordererOptions.map((o) => (
                                <option key={o} value={o}>{o}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>하위 사용 어플</label>
                        <select
                            value={form.sub_app}
                            onChange={(e) => handleChange('sub_app', e.target.value)}
                            className={selectClass}
                        >
                            <option value="">선택</option>
                            {subAppOptions.map((o) => (
                                <option key={o} value={o}>{o}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>계약 유형</label>
                        <select
                            value={form.contract_type}
                            onChange={(e) => handleChange('contract_type', e.target.value)}
                            className={selectClass}
                        >
                            <option value="">선택</option>
                            {contractTypeOptions.map((o) => (
                                <option key={o} value={o}>{o}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>공사팀 담당자</label>
                        <select
                            value={form.construction_staff}
                            onChange={(e) => handleChange('construction_staff', e.target.value)}
                            className={selectClass}
                        >
                            <option value="">선택</option>
                            {CONSTRUCTION_STAFF_OPTIONS.map((o) => (
                                <option key={o} value={o}>{o}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>세팅 담당자</label>
                        <select
                            value={form.setting_staff}
                            onChange={(e) => handleChange('setting_staff', e.target.value)}
                            className={selectClass}
                        >
                            <option value="">선택</option>
                            {SETTING_STAFF_OPTIONS.map((o) => (
                                <option key={o} value={o}>{o}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className={labelClass}>설치팀</label>
                        <select
                            value={form.installation_team}
                            onChange={(e) => handleChange('installation_team', e.target.value)}
                            className={selectClass}
                        >
                            <option value="">선택</option>
                            {INSTALLATION_TEAM_OPTIONS.map((o) => (
                                <option key={o} value={o}>{o}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
}
